<script setup lang="ts">
import { computed, useHead } from '#imports'

export interface UIIframeProps {
    src?: string
    /**
     * @description A video Cloudflare Stream ID
     * @example '0934b7c7d911b2d241174084e3e17d6c'
     */
    videoId?: string
    aspectRatio?: `${number}/${number}` | null
    loading?: 'lazy' | 'eager'
    fitParent?: boolean
    altText?: string
    load?: boolean
}

const props = withDefaults(defineProps<UIIframeProps>(), {
    aspectRatio: '16/9',
    loading: 'lazy',
    altText: 'Video',
    load: true,
})

const emit = defineEmits<{
    load: [event: Event]
}>()

const paddingTop = computed(() => {
    if (!props.aspectRatio) return undefined

    const [width, height] = props.aspectRatio.split('/')

    const percentage = Number(height) / Number(width) /* 16 / 9 Aspect Ratio (divide 9 by 16 = 0.5625) */

    return `${percentage * 100}%`
})

const cloudflareCustomerStreamUrl = 'customer-sgy3dro64g0stlfq.cloudflarestream.com'

const iframeSrc = computed(() => {
    if (props.videoId) {
        return `https://${cloudflareCustomerStreamUrl}/${props.videoId}/iframe?muted=true&loop=true&autoplay=true&controls=false${props.loading === 'eager' ? '&preload=true' : ''}&poster=https%3A%2F%2F${cloudflareCustomerStreamUrl}%2F${props.videoId}%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600`
    }

    if (props.src) {
        return props.src
    }

    console.warn('UIIframe component missing src prop or videoId.')
    return ''
})

// Preconnect to Cloudflare Stream custom url. Ships in SSR <head> so the
// TLS handshake warms during HTML parse, even though the iframe element
// itself is deferred to the client by <ClientOnly> in the template below.
if (props.loading === 'eager' && props.videoId) {
    useHead({
        link: [
            {
                rel: 'preconnect',
                href: `https://${cloudflareCustomerStreamUrl}`
            }
        ]
    })
}
</script>

<template>
    <div
        class="ui-iframe"
        :class="{ 'ui-iframe--fit-parent': props.fitParent }"
        :style="{ paddingTop }"
    >
        <ClientOnly>
            <iframe
                v-if="props.load"
                :src="iframeSrc"
                :loading="loading"
                frameborder="0"
                allowfullscreen="true"
                allow="autoplay; encrypted-media; picture-in-picture;"
                :title="altText"
                @load="(e) => emit('load', e)"
            />
        </ClientOnly>
    </div>
</template>

<style scoped lang="scss">
.ui-iframe {
    position: relative;
    width: 100%;
    height: 100%;
    pointer-events: none;
    border-radius: inherit;
    overflow: hidden;

    &--fit-parent {
        position: absolute;
        top: 0;
        left: 0;
    }

    iframe {
        position: absolute;
        border: 0;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%); /* Center the iframe */
        /* These ensure the iframe dimensions scale based on aspect-ratio and min-size */
        width: auto;
        height: auto;
        min-width: 100%;
        min-height: 100%;
        aspect-ratio: v-bind(aspectRatio);
    }
}
</style>
